import { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { CompanyInfo } from "../../Models/company";
import { useTerminology } from "../../Core/useTerminology";
import { getCompanyIconElement } from "../../Helpers/iconHelpers";
import { ComoInput } from "../Common/ComoInput";
import colors from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { UpdateEntityIcon } from "./UpdateEntityIcon";
import { enqueueSnackbar } from "notistack";

export interface EditCompanyFormData {
  name: string;
  location: string;
  industry: string;
  about: string;
  icon_id: number;
}

export interface EditCompanyModalProps {
  open: boolean;
  onClose: () => void;
  company: CompanyInfo;
  onSave: (data: EditCompanyFormData) => void;
}

export const EditCompanyModal: React.FC<EditCompanyModalProps> = ({
  open,
  onClose,
  company,
  onSave,
}) => {
  const { t } = useTranslation("singleCompanyComponent");
  const terminology = useTerminology();
  const { colors: workspace } = useWorkspaceColors();
  const [formValues, setFormValues] = useState<EditCompanyFormData>({
    name: company.name || "",
    location: company.location || "",
    industry: company.industry || "",
    about: company.about || "",
    icon_id: company.icon_id || 1,
  });

  const [iconModalOpen, setIconModalOpen] = useState(false);

  const handleIconSelect = (newIconId: number) => {
    setFormValues((prev) => ({ ...prev, icon_id: newIconId }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    const trimmedName = formValues.name.trim();

    if (!trimmedName) {
      enqueueSnackbar(
        t("editEntityModal.tooltipIncomplete", {
          entity: terminology.singular,
        }),
        { variant: "warning" }
      );
      return;
    }
    if (trimmedName.length > 40) {
      enqueueSnackbar(
        t("editEntityModal.nameTooLong", {
          entity: terminology.singular,
        }),
        { variant: "warning" }
      );
      return;
    }
    onSave({
      ...formValues,
      name: trimmedName,
      location: formValues.location.trim(),
      industry: formValues.industry.trim(),
      about: formValues.about.trim(),
    });
  };

  const handleClose = () => {
    onClose();
  };

  const labelSx = {
    "& .MuiInputLabel-root": {
      fontSize: "0.85rem",
      fontWeight: 500,
      color: colors.primary_dark,
      "&.Mui-focused": {
        color: colors.primary_dark,
      },
    },
    "& .MuiInputBase-input": {
      color: colors.primary_dark,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          padding: 1,
        },
      }}
    >
      <DialogTitle>
        <Typography sx={{ fontWeight: 600, color: workspace.primary_dark }}>
          {t("editEntityModal.title", { entity: terminology.singular })}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 80,
                height: 80,
                borderRadius: 2,
                backgroundColor: colors.gray100,
                border: `2px solid ${colors.gray300}`,
              }}
            >
              {getCompanyIconElement(formValues.icon_id, {
                color: colors.accent,
                fontSize: "3rem",
              })}
              <IconButton
                onClick={() => setIconModalOpen(true)}
                sx={{
                  position: "absolute",
                  bottom: -8,
                  right: -8,
                  backgroundColor: workspace.primary_dark,
                  color: "white",
                  width: 28,
                  height: 28,
                  "&:hover": {
                    backgroundColor: workspace.primary_dark,
                    opacity: 0.9,
                  },
                }}
              >
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>

          <ComoInput
            name="name"
            label={`${t("editEntityModal.entityName", {
              entity: terminology.singular,
            })} *`}
            placeholder={t("editEntityModal.entityName", {
              entity: terminology.singular,
            })}
            value={formValues.name}
            onChange={handleInputChange}
            primaryColor={workspace.primary_dark}
            sx={labelSx}
            InputLabelProps={{ shrink: true }}
          />

          <ComoInput
            name="location"
            label={t("singleCompanyComponent.location")}
            placeholder={t("singleCompanyComponent.location")}
            value={formValues.location}
            onChange={handleInputChange}
            primaryColor={workspace.primary_dark}
            sx={labelSx}
            InputLabelProps={{ shrink: true }}
          />

          <ComoInput
            name="industry"
            label={t("singleCompanyComponent.industry")}
            placeholder={t("singleCompanyComponent.industry")}
            value={formValues.industry}
            onChange={handleInputChange}
            primaryColor={workspace.primary_dark}
            sx={labelSx}
            InputLabelProps={{ shrink: true }}
          />

          <ComoInput
            name="about"
            label={t("singleCompanyComponent.about")}
            placeholder={t("singleCompanyComponent.about")}
            value={formValues.about}
            onChange={handleInputChange}
            multiline
            rows={4}
            primaryColor={workspace.primary_dark}
            sx={labelSx}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          sx={{
            color: colors.gray600,
            "&:hover": {
              backgroundColor: colors.gray100,
            },
          }}
        >
          {t("singleCompanyComponent.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            color: colors.gray100,
            backgroundColor: workspace.primary_dark,
            "&:hover": {
              backgroundColor: workspace.primary_dark,
              opacity: 0.9,
            },
          }}
        >
          {t("editEntityModal.save")}
        </Button>
      </DialogActions>

      <UpdateEntityIcon
        open={iconModalOpen}
        onClose={() => setIconModalOpen(false)}
        selectedIconId={formValues.icon_id}
        onSelectIcon={handleIconSelect}
      />
    </Dialog>
  );
};
